<template>
  <!-- v-dialog. containing a card with a 3 row, 2 column layouy -->
  <v-dialog 
    v-model="showValue" 
    close-on-back
    :fullscreen="$vuetify.display.width < 450 || fullscreen"
    >
    <v-card id="advanced-weather-view">
      <v-card-text class="overflow-auto">
        <h1>Just how cloudy is it in {{ locationName }} in April?
        <define-term 
          no-click
          width="25ch"
          :showFor="showFor ? 5 : 0"
          >
          <template #definition>
            <p class="intro">
            Click<v-icon class="pa-0" size="1.2em">mdi-help-circle</v-icon>for more details about the cloud cover data, statistical terms, and the El Niño & La Niña weather patterns. 
          </p>
          </template>
          <template v-slot:term="{props}">
            <v-btn v-bind="props" style="font-size: 1em;" elevation="1" icon="mdi-help-circle" @click="explainerOpen = true" tabindex="0"></v-btn>
          </template>
        </define-term>
        <cloud-data-explainer
          v-model="explainerOpen"
          :fullscreen="$vuetify.display.width < 450 || fullscreen"
          />
      </h1>
        <!-- top row -->
        <v-row class="flex-row-reverse">

          <v-col cols="12" sm="5" :order="2" :order-lg="2">
            <v-row id="query-constructor">              
              <v-col class="sentence-query mb-2" col="12">
                <label class="d-inline d-sm-block d-xl-inline" for="statistics">Show me </label>
                <select 
                  class="select-box"
                  name="statistics" 
                  id="select-statistics" 
                  v-model="selectedStat"
                  >
                  <option disabled value="">Select one</option>
                  <option value="mean">the Mean</option>
                  <option value="median">the Median</option>
                  <option value="singleyear">a Single Year</option>
                </select>
                  <label class="d-inline d-sm-block d-md-inline" for="years"> of the cloud cover for </label>
                  <select 
                    v-if="selectedStat !== 'singleyear'"
                    class="select-box"
                    name="years" 
                    id="select-years" 
                    v-model="dataSubset"
                    >
                    <option disabled value="">Select one</option>
                    <option
                      v-for="[key, value] in mapSubsets" :key="key" :value="key">{{ value }}</option>
                  </select>
                  <select 
                    v-else
                    class="select-box"
                    name="years" 
                    id="select-years" 
                    v-model="selectedYear"
                    >
                    <option disabled value="">Select one</option>
                    <option
                      v-for="year in availableYears" :key="year" :value="year">{{ year }}
                    </option>
                  </select>
              </v-col>
            </v-row>
            
            <v-row class="mb-2" id="modis-radio-group">
              <v-btn 
                class="elevation-5 my-2 mb-4 mr-2"
                size="small"
                :disabled="!(needToUpdate || !showCloudCover)"
                color="#eac402" 
                @click="updateData()"
                >
                {{ displayData ? (needToUpdate ? 'Update Map' : showCloudCover ? 'Shown on Map' : 'Show on Map') : 'Show on Map'  }}
              </v-btn>
              
              <v-radio-group 
                class="modis-radio"
                v-model="modisDataSet"  
                density="compact"
                persistent-hint
                inline
                hint="MODIS Aqua Data Set"
              >
                <v-radio
                  v-for="[key, value] in modisTimes"
                  :key="key"
                  :label="value"
                  :value="key"
                  :disabled="false"
                  color="#eac402"
                  density="compact"
                  hint="MODIS Aqua Data Set"
                ></v-radio>
              </v-radio-group>
            </v-row>
            
            <v-row id="awv-chart-placeholder" v-if="!displayCharts && displayData">
              <hr style="width:100%; margin-block: 1rem;">
              <h4>Show cloud cover statistics for currently selected location: <strong class="attention">{{ locationName }}</strong></h4>
              <v-btn 
                density="compact"
                color="#eac402"
                append-icon="mdi-chevron-triple-right"
                @click="displayCharts = true">Show details</v-btn>
            </v-row>
            
            <v-row v-if="displayCharts">  
              <div id="awv-cloud-cover-display" class="">
                <div>
                  <!-- <hr> -->
                  <!-- <h3 v-if="selectedStat !== 'singleyear'"> Cloud Cover for <strong class="attention">{{ mapSubsets.get(dataSubset) }}</strong>:</h3> -->
                  <h3> Cloud Cover for <strong class="attention">{{ locationName }}</strong>:</h3>
                  <span v-if="!inBounds" style="color: #ff0000;">Location not in bounds</span>
                  <cloud-cover-line
                    :value="locationValue"
                    :label="selectedStat === 'singleyear' ? `${selectedYear}` : statText.get(selectedStat) ?? 'Cloud Cover'"
                    :codes="skyCoverCodes"
                    :ranges="skyCoverCodeRanges"
                    :icons="skyCoverIcons"
                    variant="bold"
                    > 
                    <span v-if="selectedStat === 'singleyear'">{{ selectedYear }}</span>
                    <span v-else style="font-size: .91em;">{{ mapSubsets.get(dataSubset) }}  <br /> {{ statText.get(selectedStat) }} </span>
                  </cloud-cover-line>
                </div>
                <hr>
                <h3 style="color:#ccc;">For all years:</h3>
                <!-- cloud cover for all years at location -->
                <cloud-cover-line
                  :value="median(cloudDataNearLocation)"
                  label="Median"
                  :codes="skyCoverCodes"
                  :ranges="skyCoverCodeRanges"
                  :icons="skyCoverIcons"
                  style="color:#ccc;"
                  />
                
                <cloud-cover-line
                  :value="mean(cloudDataNearLocation)"
                  label="Mean"
                  :codes="skyCoverCodes"
                  :ranges="skyCoverCodeRanges"
                  :icons="skyCoverIcons"
                  style="color:#ccc;"
                  />
              </div>
            </v-row>
          </v-col>


          <v-col 
            :class="[
              needToUpdate || !displayData || loadingNewMap ? 'show-after' : '',
              needToUpdate ? 'need-to-update' : '', 
              !displayData ? 'no-data-shown' : '',
              ]" 
            id="awv-map" cols="12" sm="7" :order="1" :order-lg="1">
            <div class="map-colorbar">
            <location-selector
              :detect-location="showOnMap"
              v-model="location"
              label="Location"
              :map-options="mapOptions"
              :place-circle-options="placeCircleOptions"
              :selected-circle-options="selectedCircleOptions"
              :selected-cloud-cover="displayedCloudData"
              :rectangle-degrees="0.5"
              :show-cloud-cover="displayData && showCloudCover"
              @dataclick="selectedDataIndex = $event.index; selectedDataCloudCover = $event.cloudCover"
              :cloud-cover-opacity-function="transferFunction"
              :geo-json-files="eclipsePaths"
              @finishLoading="loadingNewMap = false"
              />
              <color-bar
                name="cloud-cover"
                :nsteps="20"
                label=" Cloud Cover"
                :cmap="(x: number) => [`hsla(0,0%,100%, 1)`, transferFunction(x)]"
                />
            </div>
              <div class="d-flex align-center justify-space-between">
              <span class="align-self-start text-right" id="awv-map-description"> {{ mapDescriptionText }} </span>
              <div style="display:inline-block; width: 100px;" v-if="!displayData"></div>
              <v-checkbox
                v-if="displayData"
                v-model="showCloudCover"
                label="Show Cloud Cover"
                color="#eac402"
                density="compact"
                hide-details
                />
            </div>
          </v-col>  
        </v-row>
        
        
        <v-row v-if="displayCharts">
          <v-col cols="12" sm="6" class="graph-col">
            <bar-chart
              id="cloud-histogram"
              class="elevation-5"
              :labels="skyCoverCodes.map((v) => v.includes('/') ? [v.split('/')[0] + '/', v.split('/')[1]]: v)"
              :data-label="hideHistogramSubset ? 'All Years' : 'Other Years'"
              :histogram-data="cloudDataHistogram.map((v, i) => locationHistogram.length > 0 ? v - locationHistogram[i] : v)"
              :border-width="hideHistogramSubset ? [1] : [0]"
              :colors="hideHistogramSubset ? colorMap : ['#aaa']"
              :options = "{scales: {y: {beginAtZero: true, max:20}}}"
              :bar-annotations="false"
              :bar-offset="1"
              :barAnnotationLabel="(v:number) => (v * 100/20).toFixed(0) + '%'"
              stacked
              :show-legend="dataSubset !== 'allYears'"
              :title="`Cloud Conditions for ${locationName} ${allYears[0]} - ${allYears[allYears.length - 1]}`"
              :other-datasets="hideHistogramSubset ? [] : [
                {
                  label: mapSubsets.get(dataSubset),
                  data: locationHistogram,
                  backgroundColor: colorMap, //'#c51b8a',
                  borderColor: 'black', //'#c51b8a',
                  borderWidth: 1,
                }
              ]"
              />
          </v-col>
          <v-col cols="12" sm="6" class="graph-col">
          <line-chart
            :show-legend="subsetSelected"
            class="elevation-5"
            :title="`Percent Cloud Cover for ${locationName}`"
            :scatter-data="cloudDataNearLocation"
            :scatter-options="{radius: 4 }"
            :scatter-label="!subsetSelected ? 'All Years' : 'Other Years'"
            :other-data="subsetData"
            :y-range="[-.1,1.1]"
            :x-range="[new Date(2002, 9, 8), new Date(2023, 11, 8)]"

              :y-axis-options="{
                ticks: {callback: (value: number, index: number) => {
                          if (value < 0 || value > 1) {return;}
                          return (value * 100).toFixed(0) + '%';
                          }}}"
              timeseries
              color="#aaa"
              show-scatter
              :annotations="[...skyCoverCodeRanges.map(([_,[min,max]],i) => {
                return {
                  type: 'box',
                  yMin: min/100,
                  yMax: max/100,
                  backgroundColor: colorMap[i],
                  drawTime: 'beforeDraw',
                  borderColor: colorMap[i]
                }
              }),
              ...skyCoverCodeRanges.map(([label,[min,max]],i) => {
                return {
                  type: 'line',
                  borderWidth: 0,
                  drawTime: 'beforeDatasetsDraw',
                  
                  label: {
                    display: true,
                    color: 'black',
                    backgroundColor: 'transparent',
                    content: skyCoverCodes[i],
                  },
                  xMin: new Date(2023, 0, 8),
                  yMin: (min + max) / 200,
                  yMax: (min + max) / 200,
                  };
                })
              ]"
              />
          </v-col>
        </v-row>
        
      </v-card-text>
      <v-card-actions style="border-top: 1px solid white; margin-top: 10px;">
        <v-spacer></v-spacer>
        <v-btn :size="showOnMap ? 'large' : 'default'" color="#eac402" variant="flat" @click="close()">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts"> // Options API
import { inflate } from "pako";
import { defineComponent, PropType } from 'vue';
import BarChart from './BarChart.vue';
import LineChart, {ChartDataType} from './LineChart.vue';
import LocationSelector from './LocationSelector.vue';
import CloudCoverLine from './CloudCoverLine.vue';
// import HoverTooltip from './HoverTooltip.vue';
import DefineTerm from './DefineTerm.vue';
import ColorBar from './ColorBar.vue';
import eclipseUmbra from "./assets/upath_hi.json";
import coordsEightFile from './assets/modis_eight_day/coords.zip';
import coordsOneFile from './assets/modis_one_day/coords.zip';

import { isNumber, OrderedPair, textForLocation } from './utils';
// isNumber is a utility function that checks if a value is a number
// We need to use Ordered Pairs as that is the data format that Chart.js expects
type LineGraphData = OrderedPair<Date, number>[];
import { csvParseRows } from "d3-dsv";

type CityLocation = {
  longitudeDeg: number;
  latitudeDeg: number;
};


const cityBoston: CityLocation = {
  latitudeDeg: 42.3601,
  longitudeDeg: -71.0589,
};

async function inflateFromZip(path: string): Promise<string> {
  return fetch(path)
    .then(async (response) => {
      const data = new Uint8Array(await response.arrayBuffer());
      return inflate(data, { to: 'string' });
    });
}

let latitudesEightDay: number[];
let longitudesEightDay: number[];
const coordsEightPromise = inflateFromZip(coordsEightFile)
  .then(coordsEight => {
    const points = csvParseRows(coordsEight, (row, index) => {
      if (index === 0) { return; }
      return [+row[0], +row[1]];
    });
    latitudesEightDay = points.map((p) => p[0]);
    longitudesEightDay = points.map((p) => p[1]);
  });

let latitudesOneDay: number[];
let longitudesOneDay: number[];
const coordsOnePromise = inflateFromZip(coordsOneFile)
  .then(coordsOne => {
    const points = csvParseRows(coordsOne, (row, index) => {
      if (index === 0) { return; }
      return [+row[0], +row[1]];
    });
    latitudesOneDay = points.map((p) => p[0]);
    longitudesOneDay = points.map((p) => p[1]);
  });

type Statistics = 'mean' | 'median' | 'max' | 'min' | 'singleyear';
type DataSubset = 'elNino' | 'neutral' | 'laNina' | 'allYears';
type ModisTimeSpan = '1day' | '8day' | 'monthly';

const statText = new Map([
  ['mean', 'Mean'],
  ['median', 'Median'],
  ['singleyear', 'Single Year'],
]) as Map<Statistics, string>;

function getStat(val: CloudSummaryData, stat: Exclude<Statistics,'singleyear'>): number {
  return val[stat];
}

const mapSubsets = new Map([
  ['elNino', 'El Niño Years'],
  ['neutral', 'Neutral Years'],
  ['laNina', 'La Niña Years'],
  ['allYears', 'All Years'],
]) as Map<DataSubset, string>;

const modisTimes = new Map([
  ['1day', '1 Day'],
  ['8day', '8 Day'],
]) as Map<ModisTimeSpan, string>;

const modisDirs = new Map([
  ['1day', './assets/modis_one_day/'],
  ['8day', './assets/modis_eight_day/'],
]) as Map<ModisTimeSpan, string>;

// https://colorbrewer2.org/#type=sequential&scheme=YlGnBu&n=6 //
// const _colorMap = ['#ffffcc','#c7e9b4','#7fcdbb','#41b6c4','#2c7fb8','#253494'];
// https://colorbrewer2.org/?type=sequential&scheme=YlGnBu&n=4
// const _colorMap = ['#ffffcc','#a1dab4','#41b6c4','#225ea8'].reverse();

// https://colorbrewer2.org/?type=sequential&scheme=Blues&n=4
const _colorMap = ['#eff3ff','#bdd7e7','#6baed6','#2171b5'].reverse();

// interface CloudCSVRow {
//   latitude: number;
//   longitude: number;
//   // eslint-disable-next-line @typescript-eslint/naming-convention
//   cloud_cover: number;
// }

interface CloudDataElement {
  lat: number;
  lon: number;
  cloudCover: number;
}

type CloudData = CloudDataElement[];

type CloudSummaryData = {
  lat: number;
  lon: number;
  mean: number;
  median: number;
  mode: number;
  max: number;
  min: number;
  cloudCover?: number;
};


export default defineComponent({
  name: 'AdvancedWeatherView',
  
  components: { 
    'bar-chart': BarChart,
    'line-chart': LineChart,
    'location-selector': LocationSelector,
    'cloud-cover-line': CloudCoverLine,
    'color-bar': ColorBar,
    // 'hover-tooltip': HoverTooltip,
    'define-term': DefineTerm,
  },
  
  emits: ['update:modelValue','close', 'explainer-open', 'location', 'close'],
  
  props: {
    modelValue: {
      type: Boolean,
      default: true,
      required: false,
    },
    
    defaultLocation: {
      type: Object as PropType<CityLocation> | undefined,
      default: cityBoston,
      required: true,
    },
    
    showOnMap: {
      type: Boolean,
      default: false,
      required: false,
    },
    
    showCharts: {
      type: Boolean,
      default: false,
      required: false,
    },
    
    fullscreen: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  
  data() {
    const eps = 0.000001;
    return {
      firstOpen: true,
      showFor: false,
      explainerOpen: false,
      statText,
      mapSubsets,
      modisTimes,
      modisDirs,
      modisDataSet: '8day' as ModisTimeSpan,
      location: this.defaultLocation,
      dataloaded: false,
      dataLoadingProgress: 0,
      allYears: [
        2003, 2004, 2005, 2006, 2007, 
        2008, 2009, 2010, 2011, 2012,
        2013, 2014, 2015, 2016, 2017,
        2018, 2019, 2020, 2021, 2023
      ],
      elNinoYears: [2003, 2007, 2010, 2016],
      laNinaYears: [2000, 2008, 2011, 2012, 2021, 2022, 2023],
      neutralYears: [2001, 2002, 2004, 2005, 2006, 2009, 2013, 2014, 2015, 2017, 2018],
      selectedYear: 2021,
      selectedStat: 'median' as Statistics,
      dataSubset: 'allYears' as DataSubset,
      // https://www.weather.gov/media/notification/dir/AFM_Specifications.pdf
      skyCoverCodes: ['Clear/Mostly Clear', 'Partly Cloudy', 'Mostly Cloudy', 'Cloudy/Overcast'],
      skyCoverIcons: ['mdi-weather-sunny', 'mdi-weather-partly-cloudy', 'mdi-weather-cloudy', 'mdi-clouds'],
      skyCoverCodeRanges: [
        ['clear', [0, 25]],
        ['parlyCloudy', [25 + eps, 50]],
        ['mostlyCloudy', [50 + eps, 87]],
        ['overcast', [87+eps, 100]],
      ] as [string, [number, number]][],
      colorMap: _colorMap,
      mapOptions: {
        templateUrl: "https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryTopo/MapServer/tile/{z}/{y}/{x}",
        attribution: 'Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>',
        initialLocation: {
          latitudeDeg: 35,
          longitudeDeg: -100
        },
        initialZoom: 3
      },
      eclipsePaths: [{
        geojson: eclipseUmbra as GeoJSON.GeometryCollection,
        style: {fillColor: '#333', color:'black', weight: 1, opacity: 0.5, fillOpacity: 0.5, id:"upath"}},
      {url: 'https://raw.githubusercontent.com/johnarban/wwt_interactives/main/images/center.json',
        style: {color: '#ff0000', weight: 2, opacity: 1, fillOpacity: 0}
      }],
      placeCircleOptions: {
        color: "#FF0000",
        fillColor: "#FF000088",
        fillOpacity: 0.7,
        radius: 5 
      },
      selectedCircleOptions: {
        color: "#0000FF",
        fillColor: "#0000FF88",
        fillOpacity: 0.7,
        radius: 5 
      },
      
      // some fake data for the plot
      // scatterData: evaluate(0, 2 * Math.PI, 20, noisySin) as LineGraphData,
      // lineData: evaluate(0, 2 * Math.PI, 100, sin) as LineGraphData,
      scatterData: [] as LineGraphData,
      lineData: [] as LineGraphData,
      displayedCloudData: undefined as CloudData | undefined,
      allModisData: {'1day': {}, '8day': {}} as Record<ModisTimeSpan, Record<string, CloudData>>,
      needToUpdate: false,
      latLonIndex: new Map<[number, number], number>,
      allYearsSummary: {'1day': [] as CloudSummaryData[], '8day': [] as CloudSummaryData[]} as Record<ModisTimeSpan, CloudSummaryData[]>,
      neutralYearsSummary: {'1day': [] as CloudSummaryData[], '8day': [] as CloudSummaryData[]} as Record<ModisTimeSpan, CloudSummaryData[]>,
      elNinoYearsSummary: {'1day': [] as CloudSummaryData[], '8day': [] as CloudSummaryData[]} as Record<ModisTimeSpan, CloudSummaryData[]>,
      laNinaYearsSummary: {'1day': [] as CloudSummaryData[], '8day': [] as CloudSummaryData[]} as Record<ModisTimeSpan, CloudSummaryData[]>,
      selectedDataIndex: null as number | null,
      selectedDataCloudCover: null as number | null,
      mapDescriptionText: '',
      mapDetailsText: '',
      mapShowHideText: '',
      locationName: '',
      inBounds: false,
      displayData: false,
      displayCharts: this.showCharts,
      showCloudCover: true,
      transferFunction: this.transferFunction8,
      loadingNewMap: false,
      
    };
  },
  
  mounted() {
    console.log('Advanced Weather View mounted');
    Promise.all([coordsOnePromise, coordsEightPromise]).then(() => {
      this.needToUpdate = true;
      this.checkInBounds(this.location).then((inBounds) => {
        this.inBounds = inBounds;
      });
      // create a time to simulate data loading
      this.updateLocationName();
      if (this.modelValue) {
        this.loadEightDayData().then(() => {
          console.log('preloading data');
          this.dataloaded = true;
          this.updateData(this.showOnMap);
          this.updateMapDescriptionText();
        });
      }
    });
  },

  computed: {
    
    showValue: {
      get() {
        return this.modelValue;
      },
      set(value: boolean) {
        console.log('AdvancedWeatherView showValue set to', value);
        if (!value) {
          this.$emit('close', this.location);
        }
        this.$emit('update:modelValue', value);
      },
    },
    
    allCloudData(): Record<string, CloudData> {
      return this.allModisData[this.modisDataSet];
    },
    
    availableYears() {
      // conceivably there could be years that are not available
      // we could handle that here. Otherwise this is just a passthrough
      return this.allYears;
    },
    
    subsetSelected() {
      // return !this.allYears.every(y => this.selectedYears.includes(y));
      return (this.selectedYears.length < this.allYears.length) || this.selectedStat === 'singleyear';
    },
    
    hideHistogramSubset() {
      const hide1 = this.dataSubset === 'allYears';
      const hide2 = this.selectedStat === 'singleyear';
      
      return hide1 || hide2;
    },
    
    latitudes(): number[] {
      if (this.modisDataSet === '1day') {
        return latitudesOneDay;
      }
      return latitudesEightDay;
    },
    
    longitudes(): number[] {
      if (this.modisDataSet === '1day') {
        return longitudesOneDay;
      }
      return longitudesEightDay;
    },
    
    cloudDataNearLocation(): LineGraphData | undefined {
      // get the cloud data nearest to the location for all years
      const allData = [] as LineGraphData;
      if (!this.inBounds) {return undefined;}
      if (this.allYears.length === 0) {
        console.log('no years');
        return [];
      }
      
      if (Object.keys(this.allCloudData).length != this.allYears.length) {
        console.log('all data not yet loaded');
        return [];
      }
      
      const index = this.selectedDataIndex ?? this.getLatLonIndex(this.location.latitudeDeg, this.location.longitudeDeg);
      if (index === -1 || index === null) {
        return [];
      }

      // show the lat lon of index
      
      this.allYears.map((year) => {
        allData.push({'x': new Date(year, 3, 8), 'y':this.allCloudData[year][index].cloudCover});
      });
      // console.log('index', index, this.latitudes[index], this.longitudes[index], allData.map(d => d.y));
      return allData;
    },
    
    cloudDataHistogram(): number[] {
      // get the cloudDataNearLocation
      const data = this.cloudDataNearLocation;
      if (data === undefined) {
        return [];
      }
      // bin according to skyCoverCodeRanges, not incluside of the max except for the last bin
      const hist = this.skyCoverCodeRanges.reduce((acc, [_key, range]) => {
        const count = data.filter((d) => d.y >= range[0]/100 && d.y <= range[1]/100).length;
        // acc.push(100 * count / data.length);
        acc.push(count);
        return acc;
      }, [] as number[]);
      return hist;
    },
    
    yearForLocation(): LineGraphData {
      console.log('yearForLocation');
      // get the all cloud data for this location for all years
      const allData = [] as LineGraphData;
      if (!this.inBounds) {return allData;}
      if (this.allYears.length === 0) {
        return allData;
      }
      
      if (Object.keys(this.allCloudData).length === 0) {
        return allData;
      }

      this.allYears.forEach((year: number) => {
        if (this.dataSubset === 'elNino' && !this.elNinoYears.includes(year)) {
          return;
        }
        if (this.dataSubset === 'neutral' && !this.neutralYears.includes(year)) {
          return;
        }
        if (this.dataSubset === 'laNina' && !this.laNinaYears.includes(year)) {
          return;
        }

        const data = this.allCloudData[year];
        if (data === undefined) {
          return;
        }
        const index = this.selectedDataIndex ?? this.getLatLonIndex(this.location.latitudeDeg, this.location.longitudeDeg);
        if (index === -1 || index === null) {
          return;
        }
        allData.push({'x':new Date(year, 3, 8), 'y':data[index].cloudCover});
      });

      return allData;
    },
    
    locationHistogram(): number[] {
      // get the histogram data for the cloud cover)
      if (this.dataSubset === 'allYears' || this.selectedStat == 'singleyear') {
        return [];
      }
      return this.getHistogram(this.yearForLocation.map(d => d.y), 'none');
    },
    
    
    locationValue(): number | null {
      if (!this.inBounds) {return null;}
      
      if (this.selectedStat === 'mean') {
        return this.mean(this.yearForLocation.map(d => d.y));
      }
      if (this.selectedStat === 'median') {
        return this.median(this.yearForLocation.map(d => d.y));
      }
      if (this.selectedStat === 'singleyear') {
        if (this.cloudDataNearLocation) {
          const out = this.cloudDataNearLocation.filter( v => v.x.getFullYear() === this.selectedYear);
          if (out.length > 0) {
            return out[0].y;
          }
          return null;
        }
      }
      return null;
    },

    
    elNinoData(): CloudData | undefined {
      if (this.selectedStat === 'singleyear') {
        return;
      }
      return this.getStat(this.elNinoYearsSummary[this.modisDataSet], this.selectedStat);
    },
    
    neutralData(): CloudData | undefined {
      if (this.selectedStat === 'singleyear') {
        return;
      }
      return this.getStat(this.neutralYearsSummary[this.modisDataSet], this.selectedStat);
    },
    
    laNinaData(): CloudData | undefined {
      if (this.selectedStat === 'singleyear') {
        return;
      }
      return this.getStat(this.laNinaYearsSummary[this.modisDataSet], this.selectedStat);
    },
    
    allYearsData(): CloudData | undefined {
      if (this.selectedStat === 'singleyear') {
        return;
      }
      return this.getStat(this.allYearsSummary[this.modisDataSet], this.selectedStat);
    },
    
    selectedYears(): number[] {    
      if (this.selectedStat === 'singleyear') {
        return [this.selectedYear];
      }
      
      if (this.dataSubset === 'elNino') {
        return this.elNinoYears;
      }
      
      if (this.dataSubset === 'neutral') {
        return this.neutralYears;
      }
      
      if (this.dataSubset === 'laNina') {
        return this.laNinaYears;
      }
      
      if (this.dataSubset === 'allYears') {
        return this.allYears;
      }
      
      return [];
    },
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    subsetData(): ChartDataType['datasets'] {
      if (!this.subsetSelected) { return [];}
      
      if (this.cloudDataNearLocation) {
        const data = this.cloudDataNearLocation.filter( v => this.selectedYears.includes(v.x.getFullYear()));
        if (data === undefined) {
          return [];
        }
        return [{
          type: 'scatter',
          label: this.selectedStat !== 'singleyear' ? this.mapSubsets.get(this.dataSubset)  as string : `${this.selectedYear}`,
          backgroundColor: data.map(_v => '#eac402'),
          data: data,
          pointRadius: 6,
          borderColor: 'black',
          
        }];
      }
      
      return [];
      
    },
    
  },
  
  
  
  methods: {
    
    transferFunction8(val: number | null): number {
      if (val === null) {
        return 0;
      }
      // return sigmoid
      const y = (val - 0.5) / .12;
      const z = Math.exp(y);
      return z / (1 + z);
    },
    
    transferFunction1(val: number | null): number {
      if (val === null) {
        return 0;
      }
      
      return val >= 0.05 ? 0.2 + Math.pow(val,1.5) * .8 : val;
    },
    
    getHistogram(arr: number[], norm: 'none' | 'fraction' | 'percent' = 'none'): number[] {
      // get the histogram data for the cloud cover)
      if (arr === undefined || arr === null || arr.length === 0) {
        return [];
      }
      
      const array = arr.filter(isNumber);
      const hist = this.skyCoverCodeRanges.reduce((acc, [_key, range]) => {
        const count = array.filter((d) => d >= range[0]/100 && d <= range[1]/100).length;
        if (norm === 'none') {
          acc.push(count);
        }
        if (norm === 'fraction') {
          acc.push(count / array.length);
        }
        if (norm === 'percent') {
          acc.push(100 * count / array.length);
        }
        return acc;
      }, [] as number[]);

      return hist;
    },
    
    getStat(array: CloudSummaryData[], stat: Exclude<Statistics,'singleyear'>): CloudData {
      return array.map((d) => {
        return {
          lat: d.lat,
          lon: d.lon,
          cloudCover: getStat(d, stat),
        };
      });
    },

    async inflateFromCsv(csvPath: string): Promise<string> {
      return fetch(csvPath)
        .then(async (response) => {
          const data = new Uint8Array(await response.arrayBuffer());
          return inflate(data, { to: 'string' });
        });
    },

    //
    async loadSummaryData(csvPath: string): Promise<CloudSummaryData[]> {
      return this.inflateFromCsv(csvPath)
        .then(csv => {
          return csvParseRows(csv, (row, i) => {
            if (i === 0) {return;}
            return {
              lat: +row[0],
              lon: +row[1],
              mean: +row[2],
              median: +row[3],
              mode: +row[4],
              min: +row[5],
              max: +row[6],
            } as CloudSummaryData;
          });
        });
    },

    async getElNinoData() {
      console.log('loading el nino data');
      return import(`./assets/modis_${this.modisDataSet === '1day' ? 'one': 'eight'}_day/nino_ucm.zip`)
        .then(module => this.loadSummaryData(module.default))
        .then(data => {
          this.elNinoYearsSummary[this.modisDataSet] = data;
        });
    },

    async getNeutralData() {
      console.log('loading neutral data');
      // './assets/modis_eight_day/neutral_ucm.csv'
      return import(`./assets/modis_${this.modisDataSet === '1day' ? 'one': 'eight'}_day/neutral_ucm.zip`)
        .then(module => this.loadSummaryData(module.default))
        .then(data => {
          this.neutralYearsSummary[this.modisDataSet] = data;
        });
    },
    
    async getLaNinaData() {
      return import(`./assets/modis_${this.modisDataSet === '1day' ? 'one': 'eight'}_day/nina_ucm.zip`)
        .then(module => this.loadSummaryData(module.default))
        .then(data => {
          this.laNinaYearsSummary[this.modisDataSet] = data;
        });
    },
    
    async getAllYearsData() {
      return import(`./assets/modis_${this.modisDataSet === '1day' ? 'one' : 'eight'}_day/all_years_ucm.zip`)
        .then(module => this.loadSummaryData(module.default))
        .then(data => {
          this.allYearsSummary[this.modisDataSet] = data;
        });
    },
    
    getDataForYears(years: number[]): CloudData {
      // get the cloud data for a location for a set of years
      const data = [] as CloudData;
      if (!this.inBounds) {return data;}
      
      years.map((year) => {
        if (this.allCloudData[year] === undefined) {
          return;
        }
        const index = this.selectedDataIndex ?? this.getLatLonIndex(this.location.latitudeDeg, this.location.longitudeDeg);
        if (index === -1 || index === null) {
          return;
        }
        data.push(this.allCloudData[year][index]);
      });
      return data;
    },
    
    loadEightDayData() {
      console.log('awv: loading cloud data');
      this.dataLoadingProgress = 0;
      this.allYears.map((val: number, index) => {
        import(`./assets/modis_eight_day/${val}_cloud_cover.zip`)
          .then(module => this.inflateFromCsv(module.default))
          .then(csv => {
            const data = csvParseRows(csv, (row, i) => {
              if (i === 0) {return {} as CloudDataElement;}
              return {
                lat: latitudesEightDay[i-1], // minus one cuz first row is header
                lon: longitudesEightDay[i-1],
                cloudCover: +row[0],
              } as CloudDataElement;
            });
            // skip first row
            this.allModisData['8day'][val] = data.slice(1);
            this.dataLoadingProgress = Math.ceil(((index + 1) / this.allYears.length) * 100);
          });
      });
      return new Promise((resolve) => {resolve(true);});
    },
    
    loadOneDayData() {
      console.log('awv: loading cloud data');
      this.dataLoadingProgress = 0;
      this.allYears.map((val: number, index) => {
        import(`./assets/modis_one_day/${val}_cloud_cover.zip`)
          .then(module => this.inflateFromCsv(module.default))
          .then(csv => {
            const data = csvParseRows(csv, (row, i) => {
              if (i === 0) {return {} as CloudDataElement;}
              return {
                lat: latitudesOneDay[i-1], // minus one cuz first row is header
                lon: longitudesOneDay[i-1],
                cloudCover: +row[0],
              } as CloudDataElement;
            });
            // skip first row
            this.allModisData['1day'][val] = data.slice(1);
            this.dataLoadingProgress = Math.ceil(((index + 1) / this.allYears.length) * 100);
          });
      });
      return new Promise((resolve) => {resolve(true);});
    },
    
    zip(arrays: number[][]): number[][] {
      // https://stackoverflow.com/a/10284006
      return arrays[0].map((_, i) => arrays.map((array) => array[i]));
    },
    
    async checkInBounds(location: CityLocation) {
      // check if a location is approximately within bounds
      // of the data. This will include a small area just outside
      const lat = location.latitudeDeg;
      const lon = location.longitudeDeg;
      // small angle approximation correct longitude by cos(lat)
      const distances = this.latitudes.map((lat2, i) => Math.sqrt((lat - lat2) ** 2 +  (Math.cos(((lat + lat2)/2)  * Math.PI/180) * (lon - this.longitudes[i])) ** 2));
      console.log('distances', Math.min(...distances));
      return Math.floor(Math.min(...distances) * 100) <= 25 * Math.sqrt(2);
    },
    
    getLatLonIndex(lat: number, lon: number): number {
      console.log('running getLatLonIndex');
      if (this.latitudes.length === 0) {
        return -1;
      }
      if (this.longitudes.length === 0) {
        return -1;
      }
      
      if (!this.inBounds) {
        console.log('getLatLonIndex: out of bounds');
        return -1;
      }
      // binary search using distance
      const distances = this.latitudes.map((lat2, i) => Math.sqrt((lat - lat2) ** 2 + (lon - this.longitudes[i]) ** 2));
      const minIndex = distances.indexOf(Math.min(...distances));
      // this.selectedDataIndex = minIndex;
      if (this.selectedDataIndex != minIndex) {
        this.selectedDataIndex = minIndex;
      }
      return minIndex;
    },
    
    
    
    updateData(display = true) {
      // simulate data loading
      this.displayData = display;
      if (display){
        this.showCloudCover = true;
        this.needToUpdate = false;
        this.loadingNewMap = true;
      }
      this.updateMapDescriptionText();

      if (this.modisDataSet === '1day') {
        this.transferFunction = this.transferFunction1;
      }
      if (this.modisDataSet === '8day') {
        this.transferFunction = this.transferFunction8;
      }
      
      if (this.selectedStat === 'singleyear') {
        this.displayedCloudData = this.allCloudData[this.selectedYear];
        return;
      }
      if (this.dataSubset === 'elNino') {
        console.log('el nino');
        if (this.elNinoYearsSummary[this.modisDataSet].length === 0) {
          console.log('loading el nino data');
          this.getElNinoData().then(() => {
            this.displayedCloudData = this.elNinoData;
            return;
          });
          return;
        }
        this.displayedCloudData = this.elNinoData;
        return;
      }
      if (this.dataSubset === 'neutral') {
        console.log('no el nino');
        if (this.neutralYearsSummary[this.modisDataSet].length === 0) {
          console.log('loading neutral data');
          this.getNeutralData().then(() => {
            this.displayedCloudData = this.neutralData;
            return;
          });
          return;
        }
        this.displayedCloudData = this.neutralData;
        return;
      }
      
      if (this.dataSubset === 'laNina') {
        console.log('la nina');
        if (this.laNinaYearsSummary[this.modisDataSet].length === 0) {
          console.log('loading la nina data');
          this.getLaNinaData().then(() => {
            this.displayedCloudData = this.laNinaData;
            return;
          });
          return;
        }
        this.displayedCloudData = this.laNinaData;
        return;
      }
      
      if (this.dataSubset === 'allYears') {
        console.log('all years');
        if (this.allYearsSummary[this.modisDataSet].length === 0) {
          console.log('loading all years data');
          this.getAllYearsData().then(() => {
            this.displayedCloudData = this.allYearsData;
            return;
          });
          return;
        }
        this.displayedCloudData = this.allYearsData;
        return;
      }
    },
    
    close() {
      this.showValue = false;
    },
    
    upper(word: string) {
      return  word.charAt(0).toUpperCase() + word.slice(1);
    },
    
    isLineGraphData(data: LineGraphData | (number | null)[]): data is LineGraphData {
      return (data && data[0]!=null && typeof data[0] === 'object' && 'x' in data[0]);
    },
    
    // stats methods
    mean(array: (number | null)[] | LineGraphData | undefined): number | null {
      if (array === undefined) {
        return null;
      }

      if (this.isLineGraphData(array)) {
        array = array.map((d) => d.y);
      }
      
      const arr = array.filter((v) => isNumber(v)) as number[];
      return arr.reduce((a, b) => a + b, 0) / arr.length;
    },
    
    median(array: (number | null)[] | LineGraphData | undefined): number | null {
      if (array === undefined) {
        return null;
      }

      if (this.isLineGraphData(array)) {
        array = array.map((d) => d.y);
      }
      
      const arr = array.filter((v) => isNumber(v)) as number[];
      const mid = Math.floor(arr.length / 2);
      const nums = [...arr].sort((a, b) => a - b);
      return arr.length % 2 == 0 ? (nums[mid] + nums[mid - 1]) / 2 : nums[mid];
    },
    
    updateLocationName() {
      textForLocation(this.location.longitudeDeg, this.location.latitudeDeg)
        .then((name) => {
          this.locationName = name;
        })
        .catch((e) => {
          console.error('error getting location name', e);
        });
    },
    
    updateMapDescriptionText() {
      const stat = this.selectedStat === 'singleyear' ? '' : this.statText.get(this.selectedStat);
      const subset = this.selectedStat === 'singleyear' ? this.selectedYear : this.mapSubsets.get(this.dataSubset);
      const modis = this.modisDataSet === '1day' ? '1-day' : '8-day';
      this.mapDetailsText = `  ${modis} ${stat} cloud cover for ${subset}.`;
      if (!this.displayData) {
        this.mapDescriptionText = 'Press "Show on Map" to display cloud cover data.';
      } else {
        this.mapDescriptionText = `${this.showCloudCover ? 'Displaying' : '(Hidden)'}  ${this.mapDetailsText}`;
      }
    },

    updateMapShowHideText() {
      this.mapDescriptionText = `${this.showCloudCover ? 'Displaying' : '(Hidden)'}  ${this.mapDetailsText}`;
    },
    
    getCloudCoverText(val: number | null): [number | null, string | undefined] {
      const index = this.skyCoverCodeRanges.findIndex(([_key, range]) => {
        if (val === null) {
          return false;
        }
        return val >= range[0] / 100 && val <= range[1] / 100;
      });
      return [index, this.skyCoverCodes[index]];
    },
    
    cloudIcon(val: number) {
      const [index, _text] = this.getCloudCoverText(val);
      if (index == null) {
        return 'mdi-cloud-cancel';
      } 
      return this.skyCoverIcons[index];
    }
  },
  
  watch: {
    modelValue(value: boolean) {
      if (value && Object.keys(this.allModisData['8day']).length === 0) {
        console.log('loading data');
        this.loadEightDayData().then(() => {
          console.log('finished loading data');
          this.dataloaded = true;
          this.updateData(this.displayData);
        });
        if (this.firstOpen) {
          setTimeout(() => {
            this.showFor = true;
          }, 250);
        }
      } else {
        console.log('closing AWV view');
        this.needToUpdate = true;
        this.firstOpen = false;
        // this.displayData = false;
      }

      if (value && (this.defaultLocation.latitudeDeg !== this.location.latitudeDeg || this.defaultLocation.longitudeDeg !== this.location.longitudeDeg)) {
        this.location = this.defaultLocation;
      }
    }, 
    
    defaultLocation(value: CityLocation) {
      console.log('defaultLocation', value);
      if (!this.modelValue) {
        return;
      }
      // check if they are the same, if so do nothing
      if (value.latitudeDeg === this.location.latitudeDeg && value.longitudeDeg === this.location.longitudeDeg) {
        return;
      }
      this.location = value;
    },
    
    modisDataSet(value: ModisTimeSpan) {
      console.log('modisTimespan', value);
      if (value === '1day') {
        if (Object.keys(this.allModisData['1day']).length === 0) {
          this.loadOneDayData().then(() => {
            this.needToUpdate = true;
          });
        }
      }
      this.needToUpdate = true;
    },
    
    selectedStat(value: Statistics) {
      console.log('selectedStat', value);
      this.needToUpdate = true;
    },
    
    selectedYear(value: number) {
      console.log('selectedYear', value);
      this.needToUpdate = true;
    },
    
    dataSubset(value: DataSubset) {
      console.log('dataSubset', value);
      this.needToUpdate = true;
    },
    
    dataLoadingProgress(value: number) {
      console.log('dataLoadingProgress', value);
    },
    
    location(value: CityLocation, old: CityLocation) {
      if (value.latitudeDeg === old.latitudeDeg && value.longitudeDeg === old.longitudeDeg) {
        return;
      }
      if (!this.modelValue) {
        return;
      }
      if (
        value.latitudeDeg !== this.defaultLocation.latitudeDeg
        ||
        value.longitudeDeg !== this.defaultLocation.longitudeDeg
      ) {
        this.$emit('location', value);
      }
      this.updateLocationName();
      this.checkInBounds(value).then((inBounds) => {
        this.inBounds = inBounds;
      });
      this.getLatLonIndex(value.latitudeDeg, value.longitudeDeg);
    },

    explainerOpen(open: boolean) {
      this.$emit('explainer-open', open);
    },
    
    showCloudCover() {
      this.updateMapShowHideText();
    },
  },
  
  
});
</script>


<style lang="less">

#advanced-weather-view {
  --color: #eac402;
  --default-font-size: clamp(10px, min(1.7vh, 1.7vw), 1.1rem);
  font-size: var(--default-font-size);
  --smaller-font: calc(1 * var(--default-font-size));
  
  h1 {
    font-size: 1.5em;
  }
  
  p.intro {
    font-size: 1em;
  }
  
  strong.attention {
    font-weight: bold;
    color: var(--color);
  }
  
  
  canvas.chartjs {
    border-radius: 5px;
  }
    
  .graph-col {
    height: 300px;
    padding: 6px;
  }
  
  .v-btn {
    font-size: var(--smaller-font);
  }
  
  .modis-radio {
    margin-top: 0.125em;
  }
  .v-label {
    font-size: var(--smaller-font);
  }

  
  #awv-map-description {
    font-size: 1em;
    font-weight: 500;
    margin-bottom: 1rem;
  }
  
  .map-colorbar {
    display: flex;
    max-width: 99%;
  }
  
  #awv-map {
    &.show-after .map-container::after {
      content: " ";
      
      display:flex;
      width: 100%;
      min-height: 2.5em;
      height: max-content;
      align-items: center;
      justify-content: center;
      font-size: calc(1 * var(--default-font-size));
      
      position: absolute;
      top: 0;
      left: 0;
      
      color: black;
      background-color: #cccccc77;
      z-index: 500;
      
      backdrop-filter: blur(5px) saturate(50%);
    }
    
    &.show-after.need-to-update .map-container::after {
      content: "Press 'Update Map' to view new selection";
    }
    
    &.show-after.no-data-shown .map-container::after {
      content: "Press 'Show on Map' to view data";
    }
    
    
  }
  
  .map-container {
    contain: strict;
    aspect-ratio: 1.5;
    max-height: 350px;
  }
  
  .sentence-query {
    font-weight: bold;
    font-size: 1.125em;
    
    @media (max-width: 600px) {
      font-size: 1.25em;
    }
    
    
    >label, >select {
      margin: 0.25em 0.5ch;
    }
    
    > label {
      display: block;
      font-size:1em;
    }
    
    > select {
      font-size: 1.1em;
      color:#eac402;
      
    }
  }

  .select-box {
    border-bottom: 1px solid white;
    appearance: auto !important;
  }

  #awv-cloud-cover-display {
    display: grid;
    grid-template-rows: auto auto auto;
    align-items: left;
    margin-top: 1rem;
    margin-left: 1rem;
    margin-bottom: 1rem;
    font-size: 1em;
    
    @media (max-width: 810px) {
      .label-icon-value-text-container {
        width: max-content;
      }
    }
    
  }
  
  .bold .label-icon-value-text-label {
    color:#eac402;
    font-size: 1.12em;
  }
  
  
}

</style>
